import {
  HabitSchedule,
  PlannedOccurrence,
  ScheduleEntryState,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  format,
  isAfter,
  isBefore,
  startOfDay,
} from 'date-fns';
import { isBetween } from 'shared/lib/date-fns';
import { v4 as uuidv4 } from 'uuid';

import { getScheduleEndDate } from './get-schedule-end-date';
import { getScheduleTimeframesByDateRange } from './get-schedule-timeframe-by-date';

const dateFormat = 'yyyy-MM-dd';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
};

export const getFrequencyPlanningBySchedule = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    today,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
  }: Options,
) => {
  // figure out where to start from
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleEnd = getScheduleEndDate(schedule);

  const _start = new Date(
    Math.max(
      // get the later date of the two; given startDate or schedule start date
      startDate.getTime(),
      scheduleStartDate.getTime(),
    ),
  );

  // figure out where to end
  const _end = new Date(
    Math.min(endDate.getTime(), scheduleEnd?.getTime() ?? endDate.getTime()),
  );

  // get the timeframes based on the start, end and frequency
  const count = schedule.frequency?.count ?? 0;

  const {
    skippedDates,
    completedDates,
    allCompletedDates,
    lastEntryDate,
    completedValues,
  } = schedule.entries.reduce<{
    skippedDates: Set<string>;
    completedDates: Set<string>;
    allCompletedDates: Set<string>;
    lastEntryDate?: Timestamp;
    completedValues: Map<string, number>;
  }>(
    (acc, entry) => {
      // get the skipped dates to exclude them from the plannedOccurrences
      if (
        entry.state === ScheduleEntryState.Skip ||
        entry.state === ScheduleEntryState.Failure
      ) {
        acc.skippedDates.add(format(entry.date, dateFormat));
      }

      // get the completed dates to exclude them from the plannedOccurrences
      if (entry.state === ScheduleEntryState.Complete && !!entry.value) {
        const dateString = format(entry.date, dateFormat);
        acc.allCompletedDates.add(dateString);
        acc.completedValues.set(
          dateString,
          (acc.completedValues.get(dateString) ?? 0) + entry.value,
        );

        if (uncompletedOnly) {
          acc.completedDates.add(dateString);
        }
      }

      if (
        sinceLastEntryOnly &&
        ((entry.state === ScheduleEntryState.Complete && !!entry.value) ||
          entry.state !== ScheduleEntryState.Complete)
      ) {
        acc.lastEntryDate =
          !acc.lastEntryDate ||
          acc.lastEntryDate.getTime() < entry.date.getTime()
            ? entry.date
            : acc.lastEntryDate;
      }

      return acc;
    },
    {
      skippedDates: new Set(),
      completedDates: new Set(),
      allCompletedDates: new Set(),
      lastEntryDate: undefined,
      completedValues: new Map(),
    },
  );

  // if there are timeframes from before the startDate (entries entered before the start of the schedule) we also take those
  const preTimeframes =
    _start.getTime() < scheduleStartDate.getTime()
      ? getScheduleTimeframesByDateRange(schedule, {
          startDate: _start,
          endDate: endOfDay(addDays(scheduleStartDate, -1)),
          weekStartsOn,
        })
      : [];
  const duringTimeframes = getScheduleTimeframesByDateRange(schedule, {
    startDate: scheduleStartDate,
    endDate: _end,
    weekStartsOn,
  });

  if (!preTimeframes.length) {
    // when there are no entries before the schedule startDate, the schedule startDate is the exact starting point
    duringTimeframes.forEach((timeframe) => {
      timeframe.startDate = new Date(
        Math.max(timeframe.startDate.getTime(), scheduleStartDate.getTime()),
      );
    });
  }
  // get a list of all unique timeframes
  const timeframes = [
    ...new Map(
      [...preTimeframes, ...duringTimeframes].map((t) => [
        `${t.startDate.getTime()}:${t.endDate.getTime()}`,
        t,
      ]),
    ).values(),
  ];

  // loop over timeframes
  return timeframes.reduce<PlannedOccurrence[]>((acc, timeframe) => {
    const plannedOccurrences =
      schedule.plannedOccurrences
        ?.filter(({ date }) =>
          isBetween(date, timeframe.startDate, timeframe.endDate),
        )
        .sort(
          (plannedA, plannedB) =>
            plannedA.date.getTime() - plannedB.date.getTime(),
        ) ?? [];

    if (plannedOccurrences.length) {
      acc.push(
        ...plannedOccurrences.filter(
          ({ date }) =>
            !skippedDates.has(format(date, dateFormat)) &&
            !completedDates.has(format(date, dateFormat)) &&
            (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
            // occurrences from before the start date are not wanted
            !isBefore(date, startDate) &&
            // occurrences after the end date are not wanted
            !isAfter(date, _end),
        ),
      );

      return acc;
    }

    // if no planned occurrences, we create them with the schedule timeslot and the interval
    const daysInFrame = eachDayOfInterval({
      start: timeframe.startDate,
      end: timeframe.endDate,
    });

    const completedDaysInFrame = daysInFrame.filter((date) =>
      allCompletedDates.has(format(date, dateFormat)),
    );

    // add occurrences for completed days (if we want them)
    if (!uncompletedOnly) {
      completedDaysInFrame.forEach((date) => {
        const endOfDate = endOfDay(date);
        // Validation filters
        if (
          (lastEntryDate && isBefore(date, lastEntryDate)) ||
          isBefore(date, startDate) ||
          isAfter(date, _end)
        ) {
          return;
        }

        acc.push({
          id: uuidv4(),
          date: endOfDate,
          timeslot: schedule.timeslot ?? null,
        });
      });
    }

    // if today is not in the current timeframe, there are no other occurrences to be added.
    if (!isBetween(today, timeframe.startDate, timeframe.endDate)) {
      return acc;
    }

    // add remaining occurrences on "Today" (or start/end of period)
    const completedValueInFrame = completedDaysInFrame.reduce((acc, date) => {
      const dateString = format(date, dateFormat);
      return acc + (completedValues.get(dateString) ?? 0);
    }, 0);

    const remaining = Math.max(0, count - completedValueInFrame);

    if (!remaining) {
      const nextTimeframeDate = endOfDay(addDays(timeframe.endDate, 1));
      if (
        isBetween(today, timeframe.startDate, timeframe.endDate) &&
        !isAfter(nextTimeframeDate, _end)
      ) {
        acc.push({
          id: uuidv4(),
          date: nextTimeframeDate,
          timeslot: schedule.timeslot ?? null,
        });
      }

      return acc;
    }

    const targetDate = endOfDay(
      skippedDates.has(format(today, dateFormat)) ? addDays(today, 1) : today,
    );

    const isTargetValid =
      (!lastEntryDate || !isBefore(targetDate, lastEntryDate)) &&
      !isBefore(targetDate, startDate) &&
      !isAfter(targetDate, _end);

    if (isTargetValid) {
      acc.push({
        id: uuidv4(),
        date: targetDate,
        timeslot: schedule.timeslot ?? null,
      });
    }

    return acc;
  }, []);
};
